import React, { useEffect, useState } from "react";
import { API_ORIGIN, assetUrlCandidates } from "../../api/http";
import {
  DEFAULT_PENDANT_ENGRAVING_SIZE_PX,
  getContainedImageBounds,
  getScaledEngravingFontSize,
  getScaledEngravingLetterSpacing,
  PENDANT_PHOTO_BLEED_PCT,
  PENDANT_TEXT_ALIGN,
  PENDANT_TEXT_LINE_HEIGHT,
  resolvePendantDisplayArea,
} from "../../utils/pendantPreview";

type Props = {
  selectedOptions?: Record<string, unknown>;
  snapshot?: Record<string, any>;
  size?: number;
  className?: string;
  title?: string;
};

type PendantArea = {
  shape: "circle" | "rounded-rect" | "heart";
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
  cornerRadiusPct: number;
  heartTopWidthPct: number;
  heartNotchDepthPct: number;
  heartNotchWidthPct: number;
};

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const asText = (value: unknown) => String(value ?? "");

const resolveHeartTuning = (area: Partial<PendantArea>) => {
  const heartTopWidthPct = clamp(Number(area.heartTopWidthPct ?? 34), 18, 70);
  const heartNotchDepthPct = clamp(Number(area.heartNotchDepthPct ?? 28), 8, 55);
  const maxNotchWidth = Math.max(2, Math.min(24, heartTopWidthPct - 6));
  const heartNotchWidthPct = clamp(
    Number(area.heartNotchWidthPct ?? 8),
    2,
    maxNotchWidth
  );
  return { heartTopWidthPct, heartNotchDepthPct, heartNotchWidthPct };
};

const buildHeartPath = (area: Partial<PendantArea>) => {
  const { heartTopWidthPct, heartNotchDepthPct, heartNotchWidthPct } =
    resolveHeartTuning(area);
  const halfTop = heartTopWidthPct / 2;
  const leftPeakX = 50 - halfTop;
  const rightPeakX = 50 + halfTop;
  const notchHalf = heartNotchWidthPct / 2;
  const notchLeftX = 50 - notchHalf;
  const notchRightX = 50 + notchHalf;
  const notchY = heartNotchDepthPct;
  const peakY = clamp(notchY - 17, 2, notchY - 2);
  const innerControlY = clamp(notchY - 12, peakY + 2, notchY - 2);
  const shoulderY = clamp(notchY - 4, peakY + 2, 36);
  const leftShoulderX = clamp(leftPeakX - 13, 4, leftPeakX - 2);
  const rightShoulderX = 100 - leftShoulderX;
  return [
    "M 50 95",
    `C 8 70 2 42 ${leftShoulderX.toFixed(2)} ${shoulderY.toFixed(2)}`,
    `C ${leftPeakX.toFixed(2)} ${peakY.toFixed(2)} ${notchLeftX.toFixed(
      2
    )} ${innerControlY.toFixed(2)} 50 ${notchY.toFixed(2)}`,
    `C ${notchRightX.toFixed(2)} ${innerControlY.toFixed(2)} ${rightPeakX.toFixed(
      2
    )} ${peakY.toFixed(2)} ${rightShoulderX.toFixed(2)} ${shoulderY.toFixed(2)}`,
    "C 98 42 92 70 50 95",
    "Z",
  ].join(" ");
};

const getHeartMaskDataUrl = (area: Partial<PendantArea>) => {
  const path = buildHeartPath(area);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none"><path fill="white" d="${path}"/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const getAreaBorderRadius = (area: PendantArea) => {
  if (area.shape === "circle") return "9999px";
  if (area.shape === "rounded-rect") {
    return `${clamp(Number(area.cornerRadiusPct ?? 16), 0, 50)}%`;
  }
  return undefined;
};

const coalesce = <T,>(...values: Array<T | null | undefined>) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const resolveAssetCandidates = (...values: unknown[]) => {
  const out: string[] = [];
  values.forEach((value) => {
    assetUrlCandidates(asText(value)).forEach((resolved) => {
      if (resolved && !out.includes(resolved)) out.push(resolved);
    });
  });
  return out;
};

const getPhotoImageCandidates = (...values: unknown[]) => {
  const out: string[] = [];
  const push = (value: unknown) => {
    resolveAssetCandidates(value).forEach((resolved) => {
      if (!out.includes(resolved)) out.push(resolved);
    });
  };

  values.forEach((value) => {
    if (!value) return;
    if (typeof value === "string") {
      push(value);
      return;
    }
    if (typeof value !== "object") return;
    const photo = value as Record<string, any>;
    push(photo.imageUrl);
    push(photo.sourceImageUrl);
    push(photo.url);
    push(photo.src);
    push(photo.previewUrl);
    push(photo.originalUrl);
    push(photo.publicUrl);
    push(photo.uploadUrl);
    push(photo.fileUrl);
  });

  return out;
};

const pickVariantImage = (
  snapshot: Record<string, any>,
  color: unknown,
  side: "front" | "back",
) => {
  const selectedColor = asText(color).trim().toLowerCase();
  const variantLists = [
    snapshot?.pendantColorVariants,
    snapshot?.engravingColorVariants,
    snapshot?.colorVariants,
  ];
  for (const list of variantLists) {
    if (!Array.isArray(list) || !list.length) continue;
    const match =
      list.find(
        (variant: any) =>
          selectedColor &&
          asText(variant?.color).trim().toLowerCase() === selectedColor
      ) || list[0];
    const image = side === "back" ? match?.backImage : match?.frontImage;
    if (image) return image;
  }
  return "";
};

const resolveSavedPhotoArea = (photo: any) => {
  const area = photo?.area || {};
  const bounds = photo?.renderMaskBounds || {};
  if (
    Number.isFinite(Number(bounds.xPct)) &&
    Number.isFinite(Number(bounds.yPct)) &&
    Number.isFinite(Number(bounds.widthPct)) &&
    Number.isFinite(Number(bounds.heightPct))
  ) {
    return {
      ...area,
      coordinateSpace: "container",
      xPct: Number(bounds.xPct),
      yPct: Number(bounds.yPct),
      widthPct: Number(bounds.widthPct),
      heightPct: Number(bounds.heightPct),
    };
  }
  return area;
};

function PendantFacePreview({
  side,
  size,
  baseImageUrls,
  frontPhoto,
  backPhoto,
  frontImageUrls,
  backImageUrls,
  backEngraving,
  hasFrontDesign,
  hasBackDesign,
}: {
  side: "front" | "back";
  size: number;
  baseImageUrls: string[];
  frontPhoto: any;
  backPhoto?: any;
  frontImageUrls: string[];
  backImageUrls: string[];
  backEngraving: Record<string, any>;
  hasFrontDesign: boolean;
  hasBackDesign: boolean;
}) {
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const [baseImageIndex, setBaseImageIndex] = useState(0);
  const [frontImageIndex, setFrontImageIndex] = useState(0);
  const [backImageIndex, setBackImageIndex] = useState(0);
  const baseImageKey = baseImageUrls.join("\n");
  const frontImageKey = frontImageUrls.join("\n");
  const backImageKey = backImageUrls.join("\n");
  useEffect(() => {
    setBaseImageIndex(0);
    setNaturalSize(null);
  }, [baseImageKey]);
  useEffect(() => setFrontImageIndex(0), [frontImageKey]);
  useEffect(() => setBackImageIndex(0), [backImageKey]);
  const baseImage = baseImageUrls[baseImageIndex] || "";
  const imageBounds = getContainedImageBounds(
    size,
    size,
    naturalSize?.width,
    naturalSize?.height
  );
  const frontArea = resolvePendantDisplayArea(resolveSavedPhotoArea(frontPhoto), imageBounds);
  const frontMaskDataUrl = asText(frontPhoto?.renderMaskDataUrl || "");
  const effectiveFrontMaskDataUrl =
    frontMaskDataUrl ||
    (frontArea.shape === "heart" ? getHeartMaskDataUrl(frontArea) : "");
  const backPhotoArea = resolvePendantDisplayArea(resolveSavedPhotoArea(backPhoto), imageBounds);
  const backPhotoMaskDataUrl = asText(backPhoto?.renderMaskDataUrl || "");
  const effectiveBackPhotoMaskDataUrl =
    backPhotoMaskDataUrl ||
    (backPhotoArea.shape === "heart" ? getHeartMaskDataUrl(backPhotoArea) : "");
  const backEngravingArea = resolvePendantDisplayArea(
    backEngraving.area || {
      shape: "rounded-rect",
      xPct: 0,
      yPct: 0,
      widthPct: 100,
      heightPct: 100,
      cornerRadiusPct: 0,
    },
    imageBounds
  );
  const engravingTextRaw = asText(backEngraving.text || "").trim();
  const engravingText = backEngraving.uppercase
    ? engravingTextRaw.toUpperCase()
    : engravingTextRaw;
  const frontImageUrl = frontImageUrls[frontImageIndex] || "";
  const backImageUrl = backImageUrls[backImageIndex] || "";
  const frontPhotoObjectFit =
    frontPhoto?.imageFit === "cover" ? "cover" : "contain";
  const frontPhotoFramePct =
    frontPhotoObjectFit === "cover" ? 100 + PENDANT_PHOTO_BLEED_PCT : 100;
  const backPhotoObjectFit =
    backPhoto?.imageFit === "cover" ? "cover" : "contain";
  const backPhotoFramePct =
    backPhotoObjectFit === "cover" ? 100 + PENDANT_PHOTO_BLEED_PCT : 100;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        overflow: "hidden",
        background: "#fff",
        position: "relative",
      }}
    >
      {baseImage ? (
        <img
          src={baseImage}
          alt={side === "back" ? "Pendant back preview" : "Pendant front preview"}
          onLoad={(event) => {
            const target = event.currentTarget;
            if (!target.naturalWidth || !target.naturalHeight) return;
            setNaturalSize((prev) =>
              prev?.width === target.naturalWidth &&
              prev?.height === target.naturalHeight
                ? prev
                : { width: target.naturalWidth, height: target.naturalHeight }
            );
          }}
          onError={() =>
            setBaseImageIndex((current) =>
              current + 1 < baseImageUrls.length
                ? current + 1
                : baseImageUrls.length
            )
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      ) : null}

      {side === "front" && hasFrontDesign && frontImageUrl ? (
        <div
          style={{
            position: "absolute",
            left: `${frontArea.xPct}%`,
            top: `${frontArea.yPct}%`,
            width: `${frontArea.widthPct}%`,
            height: `${frontArea.heightPct}%`,
            borderRadius:
              effectiveFrontMaskDataUrl ? undefined : getAreaBorderRadius(frontArea),
            overflow: "hidden",
            WebkitMaskImage: effectiveFrontMaskDataUrl
              ? `url(${effectiveFrontMaskDataUrl})`
              : undefined,
            maskImage: effectiveFrontMaskDataUrl
              ? `url(${effectiveFrontMaskDataUrl})`
              : undefined,
            WebkitMaskRepeat: effectiveFrontMaskDataUrl ? "no-repeat" : undefined,
            maskRepeat: effectiveFrontMaskDataUrl ? "no-repeat" : undefined,
            WebkitMaskSize: effectiveFrontMaskDataUrl ? "100% 100%" : undefined,
            maskSize: effectiveFrontMaskDataUrl ? "100% 100%" : undefined,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "#fff",
            }}
          />
          <img
            src={frontImageUrl}
            alt="Pendant customization"
            onError={() =>
              setFrontImageIndex((current) =>
                current + 1 < frontImageUrls.length
                  ? current + 1
                  : frontImageUrls.length
              )
            }
            style={{
              position: "absolute",
              left: `calc(50% + ${Number(frontPhoto?.offsetXPct || 0)}%)`,
              top: `calc(50% + ${Number(frontPhoto?.offsetYPct || 0)}%)`,
              width: `${frontPhotoFramePct}%`,
              height: `${frontPhotoFramePct}%`,
              objectFit: frontPhotoObjectFit,
              transform: `translate(-50%, -50%) rotate(${Number(
                frontPhoto?.rotation || 0
              )}deg) scale(${Number(frontPhoto?.scale || 1)})`,
              transformOrigin: "center center",
            }}
          />
        </div>
      ) : null}

      {side === "back" && backImageUrl ? (
        <div
          style={{
            position: "absolute",
            left: `${backPhotoArea.xPct}%`,
            top: `${backPhotoArea.yPct}%`,
            width: `${backPhotoArea.widthPct}%`,
            height: `${backPhotoArea.heightPct}%`,
            borderRadius:
              effectiveBackPhotoMaskDataUrl
                ? undefined
                : getAreaBorderRadius(backPhotoArea),
            overflow: "hidden",
            WebkitMaskImage: effectiveBackPhotoMaskDataUrl
              ? `url(${effectiveBackPhotoMaskDataUrl})`
              : undefined,
            maskImage: effectiveBackPhotoMaskDataUrl
              ? `url(${effectiveBackPhotoMaskDataUrl})`
              : undefined,
            WebkitMaskRepeat: effectiveBackPhotoMaskDataUrl
              ? "no-repeat"
              : undefined,
            maskRepeat: effectiveBackPhotoMaskDataUrl ? "no-repeat" : undefined,
            WebkitMaskSize: effectiveBackPhotoMaskDataUrl
              ? "100% 100%"
              : undefined,
            maskSize: effectiveBackPhotoMaskDataUrl ? "100% 100%" : undefined,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "#fff",
            }}
          />
          <img
            src={backImageUrl}
            alt="Pendant back customization"
            onError={() =>
              setBackImageIndex((current) =>
                current + 1 < backImageUrls.length
                  ? current + 1
                  : backImageUrls.length
              )
            }
            style={{
              position: "absolute",
              left: `calc(50% + ${Number(backPhoto?.offsetXPct || 0)}%)`,
              top: `calc(50% + ${Number(backPhoto?.offsetYPct || 0)}%)`,
              width: `${backPhotoFramePct}%`,
              height: `${backPhotoFramePct}%`,
              objectFit: backPhotoObjectFit,
              transform: `translate(-50%, -50%) rotate(${Number(
                backPhoto?.rotation || 0
              )}deg) scale(${Number(backPhoto?.scale || 1)})`,
              transformOrigin: "center center",
            }}
          />
        </div>
      ) : null}

      {side === "back" && !backImageUrl && hasBackDesign ? (
        <div
          style={{
            position: "absolute",
            left: `${backEngravingArea.xPct}%`,
            top: `${backEngravingArea.yPct}%`,
            width: `${backEngravingArea.widthPct}%`,
            height: `${backEngravingArea.heightPct}%`,
            borderRadius: getAreaBorderRadius(backEngravingArea),
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: `calc(50% + ${Number(backEngraving.xPct || 0)}%)`,
              top: `calc(50% + ${Number(backEngraving.yPct || 0)}%)`,
              color: asText(backEngraving.color || "#111827"),
              fontFamily: asText(backEngraving.font || "serif"),
              fontWeight: Number(backEngraving.weight || 500),
              fontStyle: backEngraving.italic ? "italic" : "normal",
              fontSize: Math.max(
                7,
                getScaledEngravingFontSize(
                  backEngraving.sizePx ?? DEFAULT_PENDANT_ENGRAVING_SIZE_PX,
                  size,
                  backEngraving.referenceSizePx
                )
              ),
              whiteSpace: "pre-wrap",
              letterSpacing: getScaledEngravingLetterSpacing(
                backEngraving.letterSpacingPx,
                size,
                backEngraving.referenceSizePx
              ),
              textAlign: asText(backEngraving.textAlign || PENDANT_TEXT_ALIGN) as
                React.CSSProperties["textAlign"],
              lineHeight: Number(
                backEngraving.lineHeight ?? PENDANT_TEXT_LINE_HEIGHT
              ),
              wordBreak: "break-word",
              transform: "translate(-50%, -50%)",
              transformOrigin: "center center",
              textShadow: "0 0 1px rgba(255,255,255,0.35)",
            }}
          >
            {engravingText}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function isBlankPreview(image: HTMLImageElement): boolean {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const context = canvas.getContext("2d");
    if (!context) return false;
    context.drawImage(image, 0, 0, 64, 64);
    const pixels = context.getImageData(0, 0, 64, 64).data;
    // The failed captures contain only white and transparent rounded corners.
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] > 16 && Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) < 245) return false;
    }
    return true;
  } catch {
    // External hosts may not allow canvas inspection. Keep their visible image.
    return false;
  }
}

function SavedPendantImage({ image, label, size, color, onUnavailable }: { image: string; label: string; size: number; color?: string; onUnavailable: () => void }) {
  const candidates = resolveAssetCandidates(image);
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [image]);
  return <div>
    <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
    {index < candidates.length ? <a href={candidates[index]} target="_blank" rel="noreferrer">
      <img src={candidates[index]} alt={color ? `${color} customized pendant ${label.toLowerCase()}` : `${label} customized pendant`} width={size} height={size} crossOrigin={candidates[index].startsWith(`${API_ORIGIN}/`) ? "anonymous" : undefined}
        onLoad={(event) => { if (isBlankPreview(event.currentTarget)) onUnavailable(); }}
        style={{ objectFit: "contain", maxWidth: "100%", height: "auto" }}
        onError={() => {
          if (index + 1 >= candidates.length) onUnavailable();
          setIndex((current) => current + 1);
        }} />
    </a> : <div role="status">Saved {label.toLowerCase()} preview is unavailable.</div>}
  </div>;
}

export default function PendantCustomizationPreview({
  selectedOptions,
  snapshot,
  size = 132,
  className,
  title = "Exact Customization Preview",
}: Props) {
  const options = (selectedOptions || {}) as Record<string, any>;
  const pendant = (options.pendantCustomization || {}) as Record<string, any>;
  const snap = (snapshot || {}) as Record<string, any>;
  const selectedColor = coalesce(options.color, pendant.color, snap.color);
  const [unusablePreview, setUnusablePreview] = useState(false);
  const previewKey = JSON.stringify(pendant.preview || {});
  useEffect(() => setUnusablePreview(false), [previewKey]);
  const canReconstruct = Boolean(pendant.frontPhoto?.imageUrl || pendant.image?.imageUrl ||
    options.photoCustomization?.imageUrl || pendant.backPhoto?.imageUrl || pendant.backEngraving?.enabled);

  const savedSides = (["front", "back"] as const).filter((side) => pendant.preview?.[`${side}Image`]);
  if (savedSides.length && !(unusablePreview && canReconstruct)) {
    return <div className={className} style={{ display: "grid", gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700 }}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {savedSides.map((side) => <SavedPendantImage key={side}
          onUnavailable={() => setUnusablePreview(true)} color={selectedColor} image={pendant.preview[`${side}Image`]} label={side === "front" ? "Front" : "Back"} size={size} />)}
      </div>
    </div>;
  }



  const frontBaseCandidates = resolveAssetCandidates(
    pendant.sourceImage,
    pendant.template?.frontImage,
    options.sourceImage,
    pickVariantImage(snap, selectedColor, "front"),
    snap.heroImage,
    snap.previewImage,
    Array.isArray(snap.moreImages) ? snap.moreImages[0] : undefined,
    Array.isArray(snap.images) ? snap.images[0] : undefined,
  );
  const rawBackBaseCandidates =
    resolveAssetCandidates(
      pendant.backImage,
      pendant.template?.backImage,
      pickVariantImage(snap, selectedColor, "back"),
      snap.backImage,
    );
  const backBaseCandidates =
    rawBackBaseCandidates.length || !pendant.doubleSided
      ? rawBackBaseCandidates
      : frontBaseCandidates;
  const frontBase = frontBaseCandidates[0] || "";
  const backBase = backBaseCandidates[0] || "";

  const originalUploads = (options.originalUploads || {}) as Record<string, any>;
  const frontPhoto = pendant.frontPhoto || pendant.image || options.photoCustomization || null;
  const frontImageUrls = getPhotoImageCandidates(
    frontPhoto,
    pendant.frontPhoto,
    pendant.image,
    options.photoCustomization,
    originalUploads.main,
    originalUploads.front,
  );
  const backPhoto = pendant.backPhoto || null;
  const backImageUrls = getPhotoImageCandidates(backPhoto, originalUploads.back);
  const backEngraving = (pendant.backEngraving || {}) as Record<string, any>;
  const engravingTextRaw = asText(backEngraving.text || "").trim();

  const hasFrontBase = !!frontBase;
  const hasBackBase = !!backBase;
  const hasFrontDesign = !!frontImageUrls.length;
  const hasBackDesign = !!backImageUrls.length || !!(backEngraving.enabled && engravingTextRaw);

  if ((!hasFrontDesign && !hasBackDesign) || (!hasFrontBase && !hasBackBase)) {
    return null;
  }

  const requestedSide = asText(pendant.activeSide || "").toLowerCase();
  const bothSides = hasFrontBase && hasBackBase;
  const singleSide: "front" | "back" =
    hasBackBase && (requestedSide === "back" || !hasFrontBase) ? "back" : "front";
  const sides: Array<"front" | "back"> = bothSides ? ["front", "back"] : [singleSide];

  const renderSide = (side: "front" | "back") => {
    const baseImage = side === "back" ? backBase : frontBase;
    if (!baseImage) return null;

    return (
      <PendantFacePreview
        side={side}
        size={size}
        baseImageUrls={side === "back" ? backBaseCandidates : frontBaseCandidates}
        frontPhoto={frontPhoto}
        backPhoto={backPhoto}
        frontImageUrls={frontImageUrls}
        backImageUrls={backImageUrls}
        backEngraving={backEngraving}
        hasFrontDesign={hasFrontDesign}
        hasBackDesign={hasBackDesign}
      />
    );
  };

  return (
    <div className={className} style={{ marginTop: 8, display: "grid", gap: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{unusablePreview ? "Customization reconstructed from saved options" : title}</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: bothSides
            ? "repeat(2, minmax(0, 1fr))"
            : "minmax(0, 1fr)",
          gap: 8,
          width: "100%",
          maxWidth: bothSides ? size * 2 + 8 : size,
        }}
      >
        {sides.map((side) => (
          <div key={side} style={{ display: "grid", gap: 4 }}>
            {bothSides ? (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "capitalize",
                }}
              >
                {side}
              </div>
            ) : null}
            <div style={{ width: "100%", maxWidth: size, aspectRatio: "1 / 1" }}>
              {renderSide(side)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
